// Label cloud body — naive word frequency for freeform-string features.
//
// Caveats (unchanged from the prior LabelCloud):
//   * Tokenization is whitespace + punctuation strip + lowercase. Multi-
//     word concepts ("birthday cake") become two unrelated tokens.
//   * A short stop-word list trims the most common English filler.
//   * Words are sized in 6 buckets so the cloud stays legible even when
//     one term dominates.

import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { tokenize } from './tokenize';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';

interface ValueRow {
  date: string;
  freeform_value: string | null;
}

const SIZE_CLASSES = ['text-sm', 'text-base', 'text-lg', 'text-xl', 'text-2xl', 'text-3xl'];

function sizeClassFor(count: number, min: number, max: number): string {
  if (max <= min) return SIZE_CLASSES[2];
  const t = (count - min) / (max - min);
  const idx = Math.min(SIZE_CLASSES.length - 1, Math.max(0, Math.round(t * (SIZE_CLASSES.length - 1))));
  return SIZE_CLASSES[idx];
}

const LabelCloudBody: React.FC<ChartBodyProps> = ({ featureId, fullscreen }) => {
  const [rows, setRows] = useState<ValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<ValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load label cloud');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const dataDates = useMemo(() => rows.map((r) => new Date(r.date).getTime()), [rows]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const tokens = useMemo(() => {
    const filtered = rows.filter((r) => withinWindow(new Date(r.date).getTime(), win));
    return tokenize(filtered);
  }, [rows, win]);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No words recorded yet for this feature.</p>
      </div>
    );
  }

  const minCount = tokens.length > 0 ? tokens[tokens.length - 1].count : 0;
  const maxCount = tokens.length > 0 ? tokens[0].count : 0;

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'}>
      <ChartTimeRangePicker state={range} />
      {tokens.length === 0 ? (
        <div className="flex items-center justify-center text-gray-500 py-8">
          <p>No words in this period.</p>
        </div>
      ) : (
        <div className={`flex flex-wrap items-center justify-start gap-x-3 gap-y-1 px-4 py-6 ${fullscreen ? 'flex-1 min-h-0 overflow-auto' : 'min-h-[18rem]'}`}>
          {tokens.map((t) => (
            <span
              key={t.word}
              title={`${t.word} (${t.count})`}
              className={`${sizeClassFor(t.count, minCount, maxCount)} text-gray-700 hover:text-amber-900 transition-colors`}
            >
              {t.word}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default LabelCloudBody;
